import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const STORAGE_KEY = 'kioskMode';

interface KioskModeContextValue {
  isKioskMode: boolean;
  enableKioskMode: () => void;
  disableKioskMode: () => void;
  toggleKioskMode: () => void;
}

const KioskModeContext = createContext<KioskModeContextValue | null>(null);

export function KioskModeProvider({ children }: { children: React.ReactNode }) {
  const [isKioskMode, setIsKioskMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isKioskMode));
    } catch {
      // Ignore storage errors
    }
  }, [isKioskMode]);

  const enableKioskMode = useCallback(() => setIsKioskMode(true), []);
  const disableKioskMode = useCallback(() => setIsKioskMode(false), []);
  const toggleKioskMode = useCallback(() => setIsKioskMode((prev) => !prev), []);

  const value = useMemo<KioskModeContextValue>(
    () => ({
      isKioskMode,
      enableKioskMode,
      disableKioskMode,
      toggleKioskMode,
    }),
    [isKioskMode, enableKioskMode, disableKioskMode, toggleKioskMode]
  );

  return <KioskModeContext.Provider value={value}>{children}</KioskModeContext.Provider>;
}

export function useKioskMode(): KioskModeContextValue {
  const ctx = useContext(KioskModeContext);
  if (!ctx) throw new Error('useKioskMode must be used within KioskModeProvider');
  return ctx;
}
